import type { ComponentProps } from 'react'

import { Button } from '@atoms'
import { DropdownMenu, DropdownMenuContent, DropdownMenuIconItem, DropdownMenuTrigger } from '@atoms/DropdownMenu'

import type { ComponentDoc, PropDocs } from '../types'

type IconItemProps = ComponentProps<typeof DropdownMenuIconItem>

type IconItemOwnProps = Pick<IconItemProps, 'icon' | 'label' | 'description' | 'href' | 'disabled' | 'className'>

const props: PropDocs<IconItemOwnProps> = {
  icon: { type: 'IconTypes', required: true, description: 'Shown in a chip before the label.' },
  label: { type: 'ReactNode', required: true, description: 'Bold first line.' },
  description: { type: 'ReactNode', description: 'Muted second line.' },
  href: { type: 'string', description: 'Makes the item a link.' },
  disabled: { type: 'boolean', description: 'Greys the item out and blocks clicks.' },
  className: {
    type: 'string',
    description: 'Extra classes for the item. Other props pass through to DropdownMenuItem.',
  },
}

function InMenu() {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button rank="secondary" trailingIcon="chevron-down">
          Open the menu
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={8} className="tw:w-80 tw:rounded-xl tw:p-2">
        <DropdownMenuIconItem
          icon="calendar"
          label="Events"
          description="Plan and roster this group's events."
          href="#"
        />
        <DropdownMenuIconItem icon="mail-01" label="Email blasts" description="Write to everyone in the group." />
        <DropdownMenuIconItem icon="send" label="SMS blasts" disabled />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const doc: ComponentDoc<IconItemProps> = {
  slug: 'dropdown-menu-icon-item',
  name: 'DropdownMenuIconItem',
  tier: 'atom',
  description:
    'A DropdownMenu item with an icon, a bold label and an optional description. Use it in menus that go to other pages.',
  importStatement: "import { DropdownMenuIconItem } from '@atoms/DropdownMenu'",
  sourcePath: 'app/frontend/components/atoms/DropdownMenu.tsx',
  component: DropdownMenuIconItem,
  props,
  examples: [
    {
      title: 'Inside a menu',
      note: 'A link, a plain item and a disabled item. The chip border changes colour on hover and focus.',
      render: () => <InMenu />,
      code: `<DropdownMenu>
  <DropdownMenuTrigger asChild>
    <Button rank="secondary" trailingIcon="chevron-down">Open the menu</Button>
  </DropdownMenuTrigger>
  <DropdownMenuContent align="start" sideOffset={8} className="tw:w-80 tw:rounded-xl tw:p-2">
    <DropdownMenuIconItem icon="calendar" label="Events" description="Plan and roster this group's events." href="/groups/1/events" />
    <DropdownMenuIconItem icon="mail-01" label="Email blasts" description="Write to everyone in the group." />
    <DropdownMenuIconItem icon="send" label="SMS blasts" disabled />
  </DropdownMenuContent>
</DropdownMenu>`,
    },
  ],
  usage: {
    do: [
      'Items that need a line of explanation.',
      'Put it in a 320px DropdownMenuContent (tw:w-80) so the description fits.',
    ],
    dont: [
      'Short action menus. Use plain DropdownMenuItem.',
      'Anything important yet. Its only use is the Group Show overflow menu, which is switched off (SHOW_OVERFLOW_MENU = false).',
    ],
  },
  a11y: [
    'Arrow keys move between items. Enter selects. Escape closes.',
    'With href it is a real link, so screen readers and middle-click work.',
  ],
}

export default doc
